import { z } from "zod/v4"
import { DocumentSnapshotLike } from "../firestore"
import { UpdatedAt } from "./system"
import { StampMissionCompleteStatus, completeStatusFromLegacyCompleted } from "./stamp-mission"

export const UserStampMissionDailyProgressDocumentMeta = z.object({
  user_id: z.string().describe("ユーザーID（コレクショングループクエリ対応のため。パスのIDはクエリ条件に使用できない制約がある）"),
  card_id: z.string().describe("親ドキュメントIDの冗長保持（コレクショングループクエリ対応のため。パスのIDはクエリ条件に使用できない制約がある）"),
  mission_id: z.string().describe("親ドキュメントIDの冗長保持（コレクショングループクエリ対応のため）"),
  day_key: z.string().describe("日付キー（例: 2025-11-06）。JST 15:00を境界とする業務日を表す"),
  day_start_at: z.date().describe("業務日の開始日時（JST 15:00）。範囲クエリ用"),
  complete_status: StampMissionCompleteStatus.optional().describe("ミッション達成ステータス。古いデータには存在しない"),
  completed: z.boolean().describe("【後方互換】ミッション完了フラグ。Flutter クライアント向けに二重書きしている"),
  completed_at: z.date().optional().describe("完了日時（JST）"),
  progress_count: z.number().optional().describe("counter型の現在達成回数。boolean型のときは未使用です"),
})

export type UserStampMissionDailyProgressDocumentMeta = z.infer<typeof UserStampMissionDailyProgressDocumentMeta>

export const UserStampMissionDailyProgressDocumentBody = z.intersection(
  UserStampMissionDailyProgressDocumentMeta,
  z.object({
    updated_at: UpdatedAt,
  })
)

export type UserStampMissionDailyProgressDocumentBody = z.infer<typeof UserStampMissionDailyProgressDocumentBody>

export type UserStampMissionDailyProgressDocument = UserStampMissionDailyProgressDocumentBody & {
  id: string, // day_key is used as document ID
}

export class UserStampMissionDailyProgressDocuments {

  static toDocumentData(doc: UserStampMissionDailyProgressDocumentBody): object {
    return {
      ...doc,
    }
  }

  /**
   * Firestore ドキュメントから UserStampMissionDailyProgressDocument を復元する。
   * 既存ドキュメントには complete_status がない場合があるため、
   * completed: boolean から complete_status へフォールバック変換する。
   */
  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): UserStampMissionDailyProgressDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    const resolveCompleteStatus = (): StampMissionCompleteStatus => {
      if (data["complete_status"] !== undefined) return data["complete_status"] as StampMissionCompleteStatus
      if (data["completed"] !== undefined) return completeStatusFromLegacyCompleted(data["completed"] as boolean)
      return "in-progress"
    }

    const completeStatus = resolveCompleteStatus()

    return {
      id: doc.id,
      user_id: data["user_id"],
      card_id: data["card_id"],
      mission_id: data["mission_id"],
      day_key: data["day_key"],
      day_start_at: data["day_start_at"],
      complete_status: completeStatus,
      completed: completeStatus !== "in-progress",
      completed_at: data["completed_at"],
      progress_count: data["progress_count"],
      updated_at: data["updated_at"],
    }
  }
}
